import React, { FC, useEffect, useState } from 'react';
import { Button, Drawer, Select } from 'antd';
import { CaretLeft, Check, Control, Hamburger } from 'common/svg';
import { SearchInput } from 'common/components/Input/SearchInput';
import { getStorageStoreCode, removeStorageStoreCode, updateStorageStoreCode } from 'entities/Store/Store.helper';

// MBC-84: sentinel mirrors StoreSelectorCir.ALL_STORES_VALUE.
const ALL_STORES_VALUE = 'all';

interface IComponentProps {
  options?: { value?: string | number; label: string }[];
  onChange: (value?: string | number) => void;
  // MBC-84: when true, prepend "All stores" option; selecting it propagates onChange(undefined).
  allowAll?: boolean;
  allowClear?: boolean;
}

export const StoreSelectorMob: FC<IComponentProps> = ({ options, onChange, allowAll, allowClear }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [value, setValue] = useState<string | number | undefined>(undefined);
  const [defaultValue] = useState(() => getStorageStoreCode());

  const effectiveOptions = allowAll
    ? [{ value: ALL_STORES_VALUE, label: 'All stores' }, ...(options || [])]
    : options || [];

  useEffect(() => {
    if (options?.length) {
      // A non-allowAll selector (e.g. Generate PO) can't aggregate — a stored 'all' from
      // another page must not leak here. Treat it as no default and pick the first store.
      const usableDefault = defaultValue && (allowAll || defaultValue !== ALL_STORES_VALUE) ? defaultValue : undefined;

      if (usableDefault) {
        setValue(usableDefault);
        onChange(usableDefault === ALL_STORES_VALUE ? undefined : usableDefault);
      } else {
        const firstValue = options[0].value;
        if (firstValue) {
          updateStorageStoreCode(firstValue.toString());
          setValue(firstValue);
          onChange(firstValue);
        }
      }
    }
  }, [options, defaultValue, allowAll]);

  const generateClassName = () => {
    let className = 'selector store-selector-mob';

    if (value) {
      className += ' selector__with-value';
    }

    return className;
  };

  const openSelector = () => {
    setOpen(true);
  };

  const closeSelector = () => {
    setSearch('');
    setOpen(false);
  };

  const clearSelection = () => {
    removeStorageStoreCode();
    setValue(undefined);
    onChange(undefined);
    closeSelector();
  };

  const handleChange = (v?: string | number) => {
    if (!v || v === value) {
      clearSelection();
      return;
    }

    updateStorageStoreCode(v.toString());
    setValue(v);
    // MBC-84: 'all' sentinel — consumer sees undefined and skips storeCode filter.
    onChange(v === ALL_STORES_VALUE ? undefined : v);
    closeSelector();
  };

  return (
    <>
      <Select
        className={generateClassName()}
        popupClassName="store-selector-mob__popup"
        suffixIcon={<Control style={{ transform: 'rotate(90deg)' }} />}
        onDropdownVisibleChange={openSelector}
        value={value}
        onChange={handleChange}
        placeholder={
          <>
            <Hamburger />
            <span>All stores</span>
          </>
        }
        labelRender={(props) => {
          return (
            <div className="selector__selection-item">
              <Hamburger />
              <span>{props.label}</span>
            </div>
          );
        }}
        options={effectiveOptions}
        allowClear={allowClear && !allowAll}
      />

      <Drawer
        open={open}
        onClose={closeSelector}
        rootClassName="store-selector-mob__dropdown"
        placement="bottom"
        height="100vh"
        title="Select a Store"
        closeIcon={<CaretLeft className="icon-gray" />}
        extra={
          value && !allowAll ? (
            <Button className="store-selector-mob__clear-option" onClick={clearSelection}>
              Clear selection
            </Button>
          ) : null
        }
      >
        <div className="mb-3">
          <SearchInput placeholder="Search by name" value={search} onChange={setSearch} />
        </div>
        <div>
          {effectiveOptions.map((item) => {
            const selected = item.value === value;

            return (
              <div
                key={item.value}
                className={`store-selector-mob__item ${selected ? 'store-selector-mob__item-selected' : ''}`}
                onClick={() => handleChange(item.value)}
              >
                <span className="store-selector-mob__item-label">{item.label}</span>
                {selected && <Check className="icon-light-purple" />}
              </div>
            );
          })}
        </div>
      </Drawer>
    </>
  );
};
